import { useState, useEffect } from 'react'

interface WhatsAppMessagesConfigProps {
  connectionId: number
  welcomeEnabled: boolean
  welcomeMessage: string | null
  closedEnabled: boolean
  closedMessage: string
  onUpdate: (config: {
    welcome_message_enabled: boolean
    welcome_message: string | null
    closed_message_enabled: boolean
    closed_message: string
  }) => void
}

export default function WhatsAppMessagesConfig({
  connectionId,
  welcomeEnabled,
  welcomeMessage,
  closedEnabled,
  closedMessage,
  onUpdate
}: WhatsAppMessagesConfigProps) {
  const [config, setConfig] = useState({
    welcomeEnabled,
    welcomeMessage: welcomeMessage || '',
    closedEnabled,
    closedMessage
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    setConfig({
      welcomeEnabled,
      welcomeMessage: welcomeMessage || '',
      closedEnabled,
      closedMessage
    })
  }, [welcomeEnabled, welcomeMessage, closedEnabled, closedMessage])

  const handleToggle = (field: 'welcomeEnabled' | 'closedEnabled') => {
    setConfig(prev => ({
      ...prev,
      [field]: !prev[field]
    }))
    setHasChanges(true)
    setSaved(false)
  }

  const handleMessageChange = (field: 'welcomeMessage' | 'closedMessage', value: string) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }))
    setHasChanges(true)
    setSaved(false)
  }

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    
    try {
      await onUpdate({
        welcome_message_enabled: config.welcomeEnabled,
        welcome_message: config.welcomeMessage || null,
        closed_message_enabled: config.closedEnabled,
        closed_message: config.closedMessage
      })
      setSaved(true)
      setHasChanges(false)
      
      setTimeout(() => setSaved(false), 3000)
    } catch (error) {
      console.error('Failed to save messages config:', error)
    } finally {
      setSaving(false)
    }
  }

  const welcomeLength = config.welcomeMessage.length
  const closedLength = config.closedMessage.length
  const maxLength = 1000

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Messages automatiques
        </h3>
        {saved && (
          <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Sauvegardé
          </span>
        )}
      </div>

      {/* Message de bienvenue */}
      <div className="space-y-3 rounded-lg border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800/50 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.welcomeEnabled}
                onChange={() => handleToggle('welcomeEnabled')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-500 dark:peer-focus:ring-green-600 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-green-600"></div>
            </label>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                Message de bienvenue
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Premier message d'un nouveau contact
              </div>
            </div>
          </div>
        </div>

        {config.welcomeEnabled && (
          <div className="space-y-2">
            <div className="relative">
              <textarea
                value={config.welcomeMessage}
                onChange={(e) => handleMessageChange('welcomeMessage', e.target.value)}
                maxLength={maxLength}
                rows={4}
                placeholder="Ex: Bonjour ! Je suis l'assistant virtuel. Comment puis-je vous aider aujourd'hui ?"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
              <div className="absolute bottom-2 right-2 text-xs text-gray-400 dark:text-gray-500">
                {welcomeLength}/{maxLength}
              </div>
            </div>
            <div className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span>💡</span>
              <span>Ce message sera envoyé uniquement à la première conversation avec un nouveau contact.</span>
            </div>
          </div>
        )}
      </div>

      {/* Message de fermeture */}
      <div className="space-y-3 rounded-lg border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800/50 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.closedEnabled}
                onChange={() => handleToggle('closedEnabled')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-500 dark:peer-focus:ring-green-600 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-green-600"></div>
            </label>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                Message hors horaires
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Réponse automatique en dehors des heures d'ouverture
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="relative">
            <textarea
              value={config.closedMessage}
              onChange={(e) => handleMessageChange('closedMessage', e.target.value)}
              maxLength={maxLength}
              rows={4}
              disabled={!config.closedEnabled}
              placeholder="Ex: Merci de nous contacter. Nos horaires sont du lundi au vendredi de 9h à 18h. Nous vous répondrons dès l'ouverture."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <div className="absolute bottom-2 right-2 text-xs text-gray-400 dark:text-gray-500">
              {closedLength}/{maxLength}
            </div>
          </div>
          <div className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>⏰</span>
            <span>
              {config.closedEnabled 
                ? "Ce message sera envoyé en dehors des horaires d'ouverture configurés."
                : "Aucun message ne sera envoyé hors horaires. L'agent répondra normalement."
              }
            </span>
          </div>
        </div>
      </div>

      {/* Informations */}
      <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900 p-3">
        <div className="flex gap-2">
          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-xs text-blue-900 dark:text-blue-100 space-y-1">
            <div><strong>Priorité des messages :</strong></div>
            <ol className="list-decimal list-inside space-y-0.5 ml-2">
              <li>Si <strong>hors horaires</strong> et message activé → Message de fermeture</li>
              <li>Si <strong>nouveau contact</strong> et message activé → Message de bienvenue</li>
              <li>Sinon → Réponse normale de l'agent avec délai configuré</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Bouton Sauvegarder */}
      <button
        type="button"
        onClick={handleSave}
        disabled={saving || !hasChanges}
        className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {saving ? (
          <>
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Sauvegarde...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Sauvegarder les messages
          </>
        )}
      </button>
    </div>
  )
}
